import {
  GET_METRIC_BY_METRIC_ID_AND_ENVS,
  governanceApiRef,
} from '@agilelab/plugin-wb-governance';
import { MetricByIdAndEnvsQuery } from '@agilelab/plugin-wb-governance-common';
import {
  customAlertApiRef,
  GenericList,
  GenericListItem,
  ThresholdResult,
} from '@agilelab/plugin-wb-platform';
import { useApolloClient } from '@apollo/client';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { Tooltip, Typography } from '@material-ui/core';
import { ReactNode, useCallback } from 'react';
import useAsync from 'react-use/lib/useAsync';

const statusIconStyle = {
  marginRight: '8px',
  display: 'flex',
  alignItems: 'center',
};

export function MetricResultsOverview({
  title,
  hideIfEmpty = false,
  noDataMessage,
  metricIdFilter,
  resource,
  wrapper,
}: {
  title?: string;
  hideIfEmpty?: boolean;
  noDataMessage?: string;
  metricIdFilter: string[];
  resource: { externalId: string; environment: string };
  wrapper?: (children: ReactNode) => JSX.Element;
}): JSX.Element {
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(customAlertApiRef);
  const governanceApi = useApi(governanceApiRef);
  const apolloClient = useApolloClient();

  const fetchMetric = useCallback(
    async (metricId: string) => {
      const { data } = await apolloClient.query<MetricByIdAndEnvsQuery>({
        query: GET_METRIC_BY_METRIC_ID_AND_ENVS,
        variables: { metricId },
      });
      return data.cgp_governance_entity[0];
    },
    [apolloClient],
  );

  const {
    loading,
    value: metricItems,
    error,
  } = useAsync(async () => {
    if (!metricIdFilter.length) return [];
    const { results } = await governanceApi.getMetricResults(
      {
        resource: resource.externalId,
        environment: resource.environment,
        metrics: metricIdFilter,
      },
      await identityApi.getCredentials(),
    );

    return Promise.all(
      results.map(async result => {
        const metric = await fetchMetric(result.metricId);
        return {
          metricName: metric.name,
          metricVersion: `v:${metric.version}`,
          metricDescription: metric.description,
          result: result.evaluationResult.thresholdResult,
          value: result.evaluationResult.value,
          error: !!result.evaluationResult.errors?.length,
        };
      }),
    );
  }, [governanceApi, identityApi, alertApi, fetchMetric]);

  const items: GenericListItem[] | undefined = metricItems?.map(v => ({
    icon: (
      <ThresholdResult
        value={v.value ?? 0}
        result={v.result}
        hasError={v.error}
        variant="reduced"
        style={statusIconStyle}
      />
    ),
    text: (
      <Tooltip title={v.metricDescription ?? v.metricName}>
        <Typography>{`${v.metricName} ${v.metricVersion}`}</Typography>
      </Tooltip>
    ),
  }));

  if (hideIfEmpty && (loading || (items && !items.length))) return <></>;

  const list = (
    <GenericList
      label={title}
      isLoading={loading}
      isError={!!error}
      error={
        error && (
          <Typography>Metric results are temporarily unavailable</Typography>
        )
      }
      items={items}
      noData={<Typography>{noDataMessage ?? 'No results'}</Typography>}
    />
  );

  return wrapper ? wrapper(list) : list;
}
